import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplace, CampaignType, Strategy, StrategyStateEnum, StrategyType } from "@front/m19-api-client";
import { StrategyEx, StrategyGroupEx } from "@front/m19-models";
import { AccountSelectionService, StrategyService } from "@front/m19-services";
import {
  IButtonComponent,
  IInputComponent,
  ISelectComponent,
  MODAL_DATA,
  ModalRef,
  Option,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  AlgoModeConfig,
  AlgoModeSelectionComponent,
} from "@m19-board/shared/algo-mode-selection/algo-mode-selection.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Observable, switchMap } from "rxjs";

export type TargetProductStrategy = {
  type: "NewProductStrategy" | "NewStrategyGroup" | "ExistingStrategy";
  productStrategy?: StrategyEx;
  productStrategyToCreate?: Strategy;
};

type SelectedProductStrategy = "NewProductStrategy" | "NewStrategy" | StrategyEx;

export interface StrategyGroupMoveProductModalData {
  strategyGroup: StrategyGroupEx;
  source: StrategyEx;
  asinsToMove: string[];
}

@UntilDestroy()
@Component({
  templateUrl: "./strategy-group-move-product-modal.component.html",
  standalone: true,
  imports: [TranslocoDirective, ISelectComponent, AlgoModeSelectionComponent, IButtonComponent, IInputComponent],
})
export class StrategyGroupMoveProductModalComponent implements OnInit {
  modalData = inject(MODAL_DATA) as StrategyGroupMoveProductModalData;
  modalRef = inject(ModalRef);

  productStrategy!: Option<SelectedProductStrategy>;
  productStrategies!: Option<SelectedProductStrategy>[];
  algoModeConfig?: AlgoModeConfig;
  algoModeConfigValid?: boolean;
  productStrategyName?: string;

  accountMarketplace = toSignal(this.accountMarketplaceSelection.singleAccountMarketplaceSelection$);

  loading = signal(false);

  constructor(
    private translocoService: TranslocoService,
    private strategyService: StrategyService,
    private toastrService: ToasterService,
    private accountMarketplaceSelection: AccountSelectionService,
  ) {}

  ngOnInit(): void {
    this.productStrategies = [
      {
        label: this.translocoService.translate("strategy-group-move-product-modal.a_new_main_strategy"),
        value: "NewProductStrategy",
      },
      {
        label: this.translocoService.translate("strategy-group-move-product-modal.a_new_strategy"),
        value: "NewStrategy",
      },
      ...this.modalData.strategyGroup.productStrategies
        .filter((s) => s.strategyId != this.modalData.source.strategyId)
        .map((s) => ({
          label: s.name ?? "",
          value: s,
        })),
    ];
    this.productStrategy = this.productStrategies[0];
  }

  close() {
    this.modalRef.close();
  }

  setProductStrategy(selected: Option<SelectedProductStrategy>) {
    this.productStrategy = selected;
  }

  setAlgoModeConfig(algoModeConfig: Partial<AlgoModeConfig>) {
    this.algoModeConfig = algoModeConfig as AlgoModeConfig;
    this.algoModeConfigValid = algoModeConfig.isValid;
  }

  setProductStrategyName(productStrategyName: string) {
    this.productStrategyName = productStrategyName;
  }

  invalid() {
    return (
      this.productStrategy == undefined ||
      (this.productStrategy.value == "NewProductStrategy" &&
        (!this.algoModeConfigValid || !this.productStrategyName || this.productStrategyName.length == 0))
    );
  }

  invalidTooltip() {
    if (this.productStrategy == undefined) {
      return "Select a main strategy to continue";
    }
    if (this.productStrategy.value == "NewProductStrategy" && !this.algoModeConfigValid) {
      return "Algorithm configuration is invalid";
    }
    if (
      (this.productStrategy.value == "NewProductStrategy" || this.productStrategy.value == "NewStrategy") &&
      (!this.productStrategyName || this.productStrategyName.length == 0)
    ) {
      return "Enter a strategy name";
    }
    return "";
  }

  save() {
    const target: TargetProductStrategy = this.getStrategyToCreate(this.productStrategy.value);
    const am = this.accountMarketplace();
    let strategyCall: Observable<any> | undefined;

    this.loading.set(true);
    if (target.type == "NewProductStrategy") {
      strategyCall = this.strategyService
        .createStrategy(
          {
            ...target.productStrategyToCreate,
            asins: this.modalData.asinsToMove.map((a) => ({ asin: a })),
          } as Strategy,
          am!.resourceOrganizationId!,
        )
        .pipe(
          switchMap(() =>
            this.strategyService.deleteAsinsFromStrategy(this.modalData.source, this.modalData.asinsToMove),
          ),
        );
    }

    if (target.type == "NewStrategyGroup") {
      strategyCall = this.strategyService.moveAsinsToNewStrategyGroup(
        am!.resourceOrganizationId!,
        am!.accountId,
        am!.marketplace,
        this.modalData.strategyGroup,
        target.productStrategyToCreate!,
        this.modalData.asinsToMove.map((a) => ({ asin: a })),
      );
    }

    if (target.type == "ExistingStrategy") {
      strategyCall = this.strategyService.moveAsinsToStrategy(
        this.modalData.asinsToMove,
        this.modalData.source,
        target.productStrategy!,
      );
    }

    if (strategyCall) {
      strategyCall.subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-group-page.asins_moved_to_another_main_strategy"),
          );
          this.modalRef.close();
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("sp-strategy-group-page.error_moving_asins_error", [error]),
            this.translocoService.translate("sp-strategy-group-page.error_moving_asins"),
          );
          this.loading.set(false);
        },
      });
    }
  }

  private getStrategyToCreate(strategyType: SelectedProductStrategy): TargetProductStrategy {
    if (strategyType !== "NewProductStrategy" && strategyType !== "NewStrategy") {
      return {
        type: "ExistingStrategy",
        productStrategy: this.productStrategy.value as StrategyEx,
      };
    }

    const strategy: Strategy = {
      accountId: this.modalData.strategyGroup.accountId!,
      marketplace: this.modalData.strategyGroup.marketplace!,
      state: StrategyStateEnum.ENABLED,
      campaignType: CampaignType.SP,
      strategyType: StrategyType.PRODUCT,
      defaultStrategy: false,
      tactics: [],
      audienceTargetings: [],
      name: this.productStrategyName,
      ...this.algoModeConfig!,
      acosTarget: this.algoModeConfig!.acosTarget ? this.algoModeConfig!.acosTarget / 100 : undefined,
      asins: [],
    };

    if (strategyType == "NewProductStrategy") {
      return {
        type: "NewProductStrategy",
        productStrategyToCreate: { ...strategy, strategyGroupId: this.modalData.strategyGroup.strategyGroupId },
      };
    } else {
      return {
        type: "NewStrategyGroup",
        productStrategyToCreate: strategy,
      };
    }
  }

  getValueFromInputEvent(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }
}
